import React, { useState, useEffect } from 'react';
import { getBankAccounts, searchCustomers, getCustomerAccountBalances } from '../services/api';
import { showSuccess, showError, showInfo } from './ToastNotifications';
import { sanitizeAmountInput, amountInputGuard, compressImage, resolveUploadUrl, formatCurrency } from '../utils/helpers';
import { amountLeft, fullyAllocated, UNATTRIBUTED_HOLDING } from '../utils/paymentTask';
import LocalImageDropBox from './LocalImageDropBox';
import './UploadReceiptModal.css';

// ---------------------------------------------------------------------------
// Shared "prepare a payment" modal, used by BOTH the customer payment_request
// flow and the platform_order flow so the two can never visually diverge.
//
// The body (bank/cash picker with balance-awareness + Insufficient handling,
// customer-front picker, multi-slip staging, Review → Confirm → prepare, the
// receipt paste/upload, the customer-proof image column) is IDENTICAL for both.
// Everything task-type-specific is injected via props:
//
//   direction             'out' (default: we PAY — pick the account to debit) or
//                         'in' (we RECEIVE — pick the account that was CREDITED).
//                         The p2p_sell leg of a platform order is the only 'in'
//                         case today. It is INJECTED rather than forked: the
//                         staging, review→confirm, receipt paste/upload and
//                         proof columns are identical; only the account picker's
//                         meaning flips. In 'in' mode the customer-front source
//                         is hidden (nobody fronts money we are receiving) and
//                         the balance/insufficient guards are skipped (a credit
//                         can never take an account negative).
//   proofs                [{url,label}] images to show beside the form. Defaults
//                         to the task's single request_proof_url.
//   proofsNote            optional line above the proof column (e.g. "3 customer
//                         receipts — record ONE PR per receipt")
//   title                 modal header text
//   renderPrimary(task)   the first header line  (Customer: X | Platform order: #id)
//   renderTotals(ctx)     the totals line under it (Requested/Paid/Remaining | Order…)
//   getPayeeText(task)    the payee banking text for the "Pay to" block + copy
//   excludeFundCustomerId customer id to hide from the funding search (or undefined)
//   paymentsListLabel     header of the "Payments already on this …" list
//   renderPaymentCancel   (p,{onCancel,submitting}) => the per-row cancel/revert button
//   fullyPreparedWarning  the yellow "already prepared — do not pay again" box body
//   confirmReceiptLine    the "receipt saved / no balance changes" confirm bullet
//   descriptionHelp       the small helper text under the Description field
//   preparedToast(a,rem)  success toast text after a prepare
//   prepareErrorMessage   default error toast if a prepare fails
//   initialPaidTotal      paid-total to seed synchronously on open
//   initialPayments       payments list to seed synchronously on open (each: {id,amount,source_type,status,url,label})
//   loadPayments()        optional async refresh of {paidTotal, payments} run after open
//   onPrepare(slips)      async submit → {paidTotal, remaining, payments?, preparedAmount}
//   onCancelPayment(p)    async cancel/revert (owns its confirm + toasts) → {paidTotal, payments?} | null
//   reloadOnError()       optional async {paidTotal, payments} used to reflect partial success on prepare error
//   onPartial / onSuccess partial-refresh / full-completion callbacks
//
// FINANCIAL DISCIPLINE (identical for both): this is a PREPARE-ONLY surface —
// no money moves and no snapshot fires here. Prepared payments can be cancelled
// (no money moved); money + checkpoint happen later at "Mark complete".
// ---------------------------------------------------------------------------
function FulfillPaymentModal({
  isOpen, onClose, task,
  direction = 'out',
  proofs,
  proofsNote,
  title,
  renderPrimary,
  renderTotals,
  getPayeeText,
  excludeFundCustomerId,
  paymentsListLabel,
  renderPaymentCancel,
  fullyPreparedWarning,
  confirmReceiptLine,
  descriptionHelp = 'Pre-filled from the payee details above. Shown on the paying account\'s transaction history.',
  preparedToast,
  prepareErrorMessage = 'Failed to prepare payment',
  initialPaidTotal = 0,
  initialPayments = [],
  loadPayments,
  onPrepare,
  onCancelPayment,
  reloadOnError,
  onSuccess,
  onPartial,
}) {
  const [bankAccounts, setBankAccounts] = useState([]);
  // The slip currently being entered:
  const [sourceType, setSourceType] = useState(''); // '' | 'bank' | 'customer'
  const [bankId, setBankId] = useState('');
  const [fundQuery, setFundQuery] = useState('');
  const [fundResults, setFundResults] = useState([]);
  const [fundSearching, setFundSearching] = useState(false);
  const [fundCustomer, setFundCustomer] = useState(null);
  const [fundBalance, setFundBalance] = useState(null); // signed MYR, null while loading
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [receiptFile, setReceiptFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  // Slips staged for this one submission, plus payments already banked earlier.
  const [staged, setStaged] = useState([]);
  const [paidTotal, setPaidTotal] = useState(0);
  const [payments, setPayments] = useState([]);
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  // ONE direction switch drives every label and every guard below.
  const inbound = direction === 'in';
  const requestedTotal = parseFloat(task?.amount || 0);
  // Live (paidTotal moves as slips are prepared/cancelled inside the modal), so
  // it reads the shared FORMULA — amountLeft, the one behind remainingToPay —
  // rather than a task field that would be stale here.
  const remaining = amountLeft(requestedTotal, paidTotal);
  const stagedSum = staged.reduce((a, s) => a + s.amount, 0);
  const leftToAllocate = Math.max(0, remaining - stagedSum);
  const completedTotal = payments
    .filter((p) => p.status === 'completed')
    .reduce((a, p) => a + parseFloat(p.amount || 0), 0);
  const preparedPending = Math.max(0, paidTotal - completedTotal);
  // Fully prepared = the whole request is already allocated to prepared/completed
  // tranches — nothing left to pay. Hide the payee details + the pay form so
  // finance can't re-copy and pay it AGAIN; it only needs completing.
  const fullyPrepared = fullyAllocated(requestedTotal, paidTotal);
  // Default the payment description to the payee bank details ("Pay to"),
  // collapsed to one line. Finance can edit it; it lands on the ledger rows so
  // it shows in the customer's and our account's transaction history.
  const payee = ((getPayeeText ? getPayeeText(task) : '') || '').toString();
  const defaultDescription = payee.replace(/\s*\n\s*/g, ' · ').trim();

  const resetSlipForm = () => {
    setSourceType(''); setBankId('');
    setFundQuery(''); setFundResults([]); setFundCustomer(null); setFundBalance(null);
    setAmount(''); setDescription(defaultDescription); setReceiptFile(null); setPreviewUrl('');
    setErrors({});
  };

  useEffect(() => {
    if (!isOpen || !task) return;
    resetSlipForm();
    setStaged([]);
    setConfirming(false);
    // Seed synchronously so the totals/remaining don't flash while any async
    // refresh runs (payment_request seeds from the task; platform passes 0/[]).
    setPaidTotal(parseFloat(initialPaidTotal || 0));
    setPayments(Array.isArray(initialPayments) ? initialPayments : []);
    (async () => {
      try {
        const data = await getBankAccounts();
        const all = data.accounts || [];
        setBankAccounts(all.filter((a) => a.account_type === 'bank' || a.account_type === 'cash'));
      } catch (e) {
        showError('Failed to load accounts');
      }
      // Async authoritative refresh (platform reads platform_order_payments).
      if (loadPayments) {
        try {
          const r = await loadPayments();
          setPaidTotal(parseFloat(r.paidTotal || 0));
          setPayments(Array.isArray(r.payments) ? r.payments : []);
        } catch (e) { /* loadPayments surfaces its own error */ }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, task]);

  // Funding-customer typeahead (min 2 chars, debounced). The request customer
  // is excluded — they can't front their own payout.
  useEffect(() => {
    if (!isOpen || sourceType !== 'customer' || fundCustomer) return;
    const q = fundQuery.trim();
    if (q.length < 2) { setFundResults([]); return; }
    let active = true;
    setFundSearching(true);
    const t = setTimeout(async () => {
      try {
        const data = await searchCustomers(q);
        if (active) setFundResults((Array.isArray(data) ? data : []).filter((c) => c.id !== excludeFundCustomerId));
      } catch (e) {
        if (active) setFundResults([]);
      } finally {
        if (active) setFundSearching(false);
      }
    }, 250);
    return () => { active = false; clearTimeout(t); };
  }, [fundQuery, sourceType, fundCustomer, isOpen, excludeFundCustomerId]);

  const selectFundCustomer = async (c) => {
    setFundCustomer(c);
    setFundResults([]);
    setFundQuery('');
    setFundBalance(null);
    try {
      const res = await getCustomerAccountBalances(c.id);
      const myr = (res?.data || []).find((b) => b.currency_type === 'MYR');
      setFundBalance(myr ? parseFloat(myr.balance) : 0);
    } catch (e) {
      setFundBalance(0);
    }
  };

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type || !file.type.startsWith('image/')) { showError('Please select an image file'); return; }
    // Compress the receipt on the user's device before holding it for upload.
    // Fall back to the original if compression fails so submission still works.
    let receipt = file;
    try {
      const { file: compressed } = await compressImage(file);
      receipt = compressed;
    } catch (err) {
      console.warn('Receipt compression failed; using original:', err);
    }
    if (receipt.size > 10 * 1024 * 1024) { showError('Image is too large even after compression (max 10MB)'); return; }
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setReceiptFile(receipt);
    setPreviewUrl(URL.createObjectURL(receipt));
  };

  // Paste the receipt screenshot anywhere in the modal (Ctrl/Cmd+V).
  const handleOverlayPaste = (e) => {
    if (submitting) return;
    const items = e.clipboardData?.items || [];
    for (let i = 0; i < items.length; i++) {
      if (items[i].type && items[i].type.indexOf('image') !== -1) {
        handleFile(items[i].getAsFile());
        break;
      }
    }
  };

  const selectedBank = bankAccounts.find((a) => String(a.id) === String(bankId));
  const slipFormTouched = () => amount !== '' || sourceType !== '' || !!receiptFile;

  // Balance-awareness: compare the amount actually being prepared (or, before
  // it's typed, the remaining-to-allocate default) against each bank/cash
  // account's current balance, so finance sees an account that can't cover
  // the payment before hitting the server's prepare-time floor guard.
  // Money IN never draws an account down, so it is never "insufficient".
  const parsedSlipAmount = parseFloat(amount);
  const effectiveSlipAmount = (!isNaN(parsedSlipAmount) && parsedSlipAmount > 0) ? parsedSlipAmount : leftToAllocate;
  // Frozen money (2026-10-23) cannot be paid out: compare against what is
  // actually available (balance − frozen), like the server's reminder does.
  const availableOf = (a) => parseFloat(a.current_balance || 0) - parseFloat(a.frozen_amount || 0);
  const isBankInsufficient = (a) => !inbound && availableOf(a) < effectiveSlipAmount - 0.01;
  // REMINDER, not a refusal (operator 2026-10-09: "want become no enough
  // balance also able to submit … add a reminder mention balance but still
  // able to submit receipt, but dont allow the payment to be mark as complete
  // if no enough balance"). Finance prepares the transfer in the bank first,
  // tops the account up, then approves — so a shortfall only blocks "Mark
  // complete" (the server's hard floor), never the prepare.
  const insufficientFundsMsg = (a, amt) =>
    `Reminder: ${a.bank_name} ${a.account_name || ''} has ${formatCurrency(availableOf(a))} available${parseFloat(a.frozen_amount || 0) > 0 ? ` (${formatCurrency(a.frozen_amount)} is frozen)` : ''} but this prepares ${formatCurrency(amt)}. You can still prepare it — it cannot be marked complete until the balance covers it.`;
  const selectedBankInsufficient = sourceType === 'bank' && !!selectedBank && isBankInsufficient(selectedBank);

  const validateSlip = () => {
    const err = {};
    const amt = parseFloat(amount);
    if (!amount || isNaN(amt) || amt <= 0) err.amount = 'Enter how much you are paying now';
    else if (amt > leftToAllocate + 0.01) err.amount = `Only ${formatCurrency(leftToAllocate)} left to allocate on this request`;
    if (!sourceType) err.source = inbound ? 'Select the bank/cash account that was credited' : 'Choose bank/cash or customer';
    if (sourceType === 'bank' && !bankId) err.source = inbound ? 'Select the bank/cash account that received the money' : 'Select the bank/cash account to pay from';
    // (A low balance is a reminder shown under the account picker, not a
    // validation error — see insufficientFundsMsg.)
    if (sourceType === 'customer' && !fundCustomer) err.source = 'Search and select the funding customer';
    // Money IN (P2P sell): the customer's payment receipt already lives on the
    // ORDER (sales uploaded it; finance verified it) — a bank-in proof here is
    // optional supporting evidence, not a gate.
    if (!inbound && !receiptFile) err.receipt = 'Receipt image is required';
    setErrors(err);
    return Object.keys(err).length === 0 ? err : null;
  };

  // Turn the current form into a staged slip object (or null if invalid).
  const buildCurrentSlip = () => {
    if (validateSlip() === null) return null; // errors set
    return {
      key: `${Date.now()}-${Math.round(parseFloat(amount) * 100)}-${staged.length}`,
      amount: parseFloat(amount),
      source_type: sourceType,
      debited_bank_account_id: sourceType === 'bank' ? bankId : null,
      debited_customer_id: sourceType === 'customer' ? fundCustomer.id : null,
      bankLabel: sourceType === 'bank' && selectedBank ? `${selectedBank.bank_name} - ${selectedBank.account_name}` : '',
      fundCustomerName: sourceType === 'customer' ? fundCustomer.customer_name : '',
      description: (description || '').trim(),
      receiptFile,
      previewUrl,
    };
  };

  const handleAddAnother = () => {
    const slip = buildCurrentSlip();
    if (!slip) return;
    setStaged((prev) => [...prev, slip]);
    // Keep the staged slip's previewUrl alive; just clear the form for the next.
    setSourceType(''); setBankId('');
    setFundQuery(''); setFundResults([]); setFundCustomer(null); setFundBalance(null);
    setAmount(''); setDescription(defaultDescription); setReceiptFile(null); setPreviewUrl('');
    setErrors({});
  };

  const removeStaged = (key) => {
    setStaged((prev) => {
      const gone = prev.find((s) => s.key === key);
      if (gone?.previewUrl) URL.revokeObjectURL(gone.previewUrl);
      return prev.filter((s) => s.key !== key);
    });
  };

  const handleReview = (e) => {
    e.preventDefault();
    let all = staged;
    if (slipFormTouched()) {
      const slip = buildCurrentSlip();
      if (!slip) return; // validation errors shown
      all = [...staged, slip];
      setStaged(all);
      setSourceType(''); setBankId('');
      setFundQuery(''); setFundResults([]); setFundCustomer(null); setFundBalance(null);
      setAmount(''); setDescription(defaultDescription); setReceiptFile(null); setPreviewUrl('');
    }
    if (all.length === 0) { setErrors({ amount: 'Add at least one payment' }); return; }
    // Low-balance slips are REMINDED, not refused (2026-10-09): the server's
    // prepare route also answers with balance_warnings, which handleConfirm
    // surfaces; the hard floor is at "Mark complete".
    for (const s of all) {
      if (!inbound && s.source_type === 'bank') {
        const bank = bankAccounts.find((a) => String(a.id) === String(s.debited_bank_account_id));
        if (bank && availableOf(bank) < s.amount - 0.01) showInfo(insufficientFundsMsg(bank, s.amount));
      }
    }
    setErrors({});
    setConfirming(true);
  };

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      const result = await onPrepare(staged);

      staged.forEach((s) => s.previewUrl && URL.revokeObjectURL(s.previewUrl));

      // Prepare-only: money moves later at "Mark complete", so a submission
      // never fully-pays by itself. Show the prepared toast, refresh the list,
      // and close only once the whole amount is allocated (fully prepared).
      const prepared = result?.preparedAmount ?? stagedSum;
      const remainingAfter = parseFloat(result?.remaining ?? 0);
      showSuccess(preparedToast(prepared, remainingAfter));
      // The server's own projection (it also counts the account's OTHER
      // still-prepared draws): say it once per short account.
      (result?.balanceWarnings || []).forEach((w) => showInfo(w.message || String(w)));
      if (result?.paidTotal !== undefined) setPaidTotal(parseFloat(result.paidTotal));
      if (result?.payments !== undefined) setPayments(result.payments);
      setStaged([]);
      setConfirming(false);
      if (onPartial) onPartial(); // refresh the list WITHOUT closing this modal
      if (remainingAfter <= 0.01) {
        if (onSuccess) onSuccess();
        setTimeout(() => onClose(), 600);
      }
    } catch (err) {
      setConfirming(false);
      showError(err.response?.data?.error || err.message || prepareErrorMessage);
      // Claim-gate conflict: the prepare routes require you still hold the claim
      // (and the task still be pending). A 403/409 here means our snapshot was
      // stale — the task changed hands or was completed elsewhere. Refresh the
      // parent list so it reflects reality (denorm Phase 6 / EX3). onPartial
      // refreshes without closing the modal.
      const conflictStatus = err.response?.status;
      if ((conflictStatus === 403 || conflictStatus === 409) && onPartial) onPartial();
      // A looped multi-slip submit (platform) can partially succeed. Drop the
      // slips that DID prepare so a retry can't double-submit them; keep the
      // failed ones staged so finance can retry only those. payment_request
      // submits are atomic and never set succeededKeys, so this is a no-op.
      if (Array.isArray(err?.succeededKeys) && err.succeededKeys.length) {
        setStaged((prev) => {
          prev.filter((s) => err.succeededKeys.includes(s.key) && s.previewUrl)
            .forEach((s) => URL.revokeObjectURL(s.previewUrl));
          return prev.filter((s) => !err.succeededKeys.includes(s.key));
        });
      }
      // Reflect any partial success (e.g. a looped multi-slip submit that failed
      // midway) by re-reading the authoritative list. payment_request submits are
      // atomic and pass no reloadOnError, so this is a no-op for them.
      if (reloadOnError) {
        try {
          const r = await reloadOnError();
          setPaidTotal(parseFloat(r.paidTotal || 0));
          setPayments(Array.isArray(r.payments) ? r.payments : []);
        } catch (e) { /* ignore */ }
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Undo one payment without leaving the modal. The task-specific handler owns
  // its own confirm dialog + toasts and returns the refreshed {paidTotal, payments?}
  // (or null if the user declined / it failed).
  const handleCancelPayment = async (p) => {
    setSubmitting(true);
    try {
      const result = await onCancelPayment(p);
      if (result) {
        if (result.paidTotal !== undefined) setPaidTotal(parseFloat(result.paidTotal));
        if (result.payments !== undefined) setPayments(result.payments);
        else setPayments((prev) => prev.filter((x) => x.id !== p.id));
        if (onPartial) onPartial();
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Copy the raw payment details for pasting into a banking app: the payee
  // block (name / bank / account, one per line) + the amount as a bare number
  // (no RM, no thousands separators). Uses the amount being paid now, or the
  // remaining amount if the field is empty.
  const handleCopyPaymentDetails = async () => {
    const lines = payee.split('\n').map((l) => l.trim()).filter(Boolean);
    const a = parseFloat(amount);
    const amt = (!isNaN(a) && a > 0) ? a : leftToAllocate;
    const text = [...lines, amt.toFixed(2)].join('\n');
    try {
      await navigator.clipboard.writeText(text);
      showSuccess('Payment details copied');
    } catch (e) {
      showError('Copy failed — select the details manually');
    }
  };

  if (!isOpen || !task) return null;

  // Stored '/uploads/…' paths resolve against the API host, which is a different
  // origin in dev (:5002) — resolveUploadUrl, the one resolver. Without it the
  // proof column silently renders broken images on a developer's machine.
  const proofList = (Array.isArray(proofs) ? proofs : (task.request_proof_url
    ? [{ url: task.request_proof_url, label: "Customer's proof (payee bank details)" }]
    : [])).filter((p) => p && p.url);
  const stagedCompletes = stagedSum >= remaining - 0.01;

  return (
    // Deliberately NO close-on-overlay-click: a mis-click outside the box must
    // not throw away a half-filled payment. Close via the × or Close buttons.
    <div className="modal-overlay" onPaste={handleOverlayPaste} onClick={(e) => e.stopPropagation()}>
      <div
        className="modal-content upload-receipt-modal"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: proofList.length > 0 ? 940 : 560, width: '95%' }}
      >
        <div className="modal-header">
          <h3>{title}</h3>
          <button onClick={() => !submitting && onClose()} className="close-button" disabled={submitting}>&times;</button>
        </div>
        <div className="modal-body" style={{ display: 'flex', gap: 20, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 420px', minWidth: 320 }}>
            <div className="form-group">
              <div>{renderPrimary(task)}</div>
              <div style={{ marginTop: 4 }}>
                {renderTotals({ requestedTotal, paidTotal, remaining, completedTotal, preparedPending, formatCurrency, fmt: formatCurrency })}
              </div>
              {payee && !inbound && !fullyPrepared && (
                <div style={{ marginTop: 6 }}>
                  <strong>Pay to:</strong>
                  <pre style={{ whiteSpace: 'pre-wrap', margin: '4px 0', fontFamily: 'inherit' }}>{payee}</pre>
                  <button
                    type="button"
                    className="btn btn-small btn-outline"
                    onClick={handleCopyPaymentDetails}
                    disabled={submitting}
                    title="Copy the payee details + amount (bare format) to paste into your banking app"
                  >
                    📋 Copy payment details
                  </button>
                </div>
              )}
              {fullyPrepared && (
                <div style={{ marginTop: 8, padding: 12, borderRadius: 8, background: '#fffbeb', border: '1px solid #fcd34d', fontSize: 13 }}>
                  {fullyPreparedWarning}
                </div>
              )}
            </div>

            {payments.length > 0 && (
              <div className="form-group" style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 10 }}>
                <label className="form-label" style={{ marginBottom: 6 }}>{paymentsListLabel}</label>
                {payments.map((p, pi) => (
                  <div key={p.id || pi} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', borderBottom: pi < payments.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                    <span>
                      {formatCurrency(p.amount)} · {p.label}
                      {p.status === 'prepared'
                        ? <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 700, color: '#b45309' }}>· PREPARED (awaiting bank)</span>
                        : <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 700, color: '#166534' }}>· COMPLETED</span>}
                      {p.url && <> · <a href={p.url} target="_blank" rel="noopener noreferrer">receipt</a></>}
                    </span>
                    {renderPaymentCancel(p, { onCancel: () => handleCancelPayment(p), submitting })}
                  </div>
                ))}
              </div>
            )}

            {/* Slips staged for THIS submission (sent together in one shot). */}
            {staged.length > 0 && (
              <div className="form-group" style={{ border: '1px solid #bfdbfe', background: '#eff6ff', borderRadius: 8, padding: 10 }}>
                <label className="form-label" style={{ marginBottom: 6 }}>{inbound ? 'Payment received' : 'Paying now'} — {staged.length} {inbound ? 'PR' : 'slip'}{staged.length === 1 ? '' : 's'} in one submission</label>
                {staged.map((s) => (
                  <div key={s.key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0' }}>
                    <span>
                      {formatCurrency(s.amount)} · {s.source_type === 'customer' ? `${s.fundCustomerName} fronts` : (s.bankLabel || 'bank/cash')}
                      {s.description && <span style={{ display: 'block', fontSize: 12, color: '#6b7280' }}>“{s.description}”</span>}
                    </span>
                    <button type="button" className="btn btn-small btn-secondary" onClick={() => removeStaged(s.key)} disabled={submitting}>
                      Remove
                    </button>
                  </div>
                ))}
                <div style={{ marginTop: 6, fontWeight: 600 }}>
                  {inbound ? 'Total received' : 'Total now'}: {formatCurrency(stagedSum)} · Left to allocate: {formatCurrency(leftToAllocate)}
                </div>
              </div>
            )}

            {fullyPrepared ? (
              <div className="form-actions">
                <button type="button" onClick={() => !submitting && onClose()} className="btn btn-secondary" disabled={submitting}>Close</button>
              </div>
            ) : !confirming ? (
              <form onSubmit={handleReview}>
                <div className="form-group">
                  <label className="form-label">{inbound ? 'How much was received? (MYR)' : 'How much are you paying now? (MYR)'} <span className="required">*</span></label>
                  <input
                    type="number"
                    className={`form-control ${errors.amount ? 'error' : ''}`}
                    step="0.01" min="0.01"
                    placeholder={`Up to ${leftToAllocate.toFixed(2)}`}
                    value={amount}
                    onChange={(e) => setAmount(sanitizeAmountInput(e.target.value))}
                    {...amountInputGuard}
                    disabled={submitting}
                  />
                  <small className="form-text">
                    Left to allocate: <strong>{formatCurrency(leftToAllocate)}</strong>. {inbound ? 'Add several PRs if the customer paid into different accounts.' : 'Add several slips to pay from different sources in one submission.'}
                  </small>
                  {errors.amount && <span className="error-message">{errors.amount}</span>}
                </div>

                <div className="form-group">
                  <label className="form-label">Description</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder={inbound ? 'e.g. the payer name shown on the bank credit' : 'e.g. the payee bank details this slip was paid to'}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    disabled={submitting}
                    maxLength={200}
                  />
                  <small className="form-text">{descriptionHelp}</small>
                </div>

                <div className="form-group">
                  <label className="form-label">{inbound ? 'Received into' : 'Pay from'} <span className="required">*</span></label>
                  <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                    <button
                      type="button"
                      className={`btn ${sourceType === 'bank' ? 'btn-primary' : 'btn-outline'}`}
                      onClick={() => { setSourceType('bank'); setFundCustomer(null); setFundQuery(''); }}
                      disabled={submitting}
                    >
                      Bank / Cash
                    </button>
                    {/* Nobody "fronts" money we are RECEIVING — the customer-front
                        source only exists on an outgoing payment. */}
                    {!inbound && (
                      <button
                        type="button"
                        className={`btn ${sourceType === 'customer' ? 'btn-primary' : 'btn-outline'}`}
                        onClick={() => { setSourceType('customer'); setBankId(''); }}
                        disabled={submitting}
                      >
                        Customer account
                      </button>
                    )}
                  </div>

                  {sourceType === 'bank' && (
                    <>
                      <select
                        className="form-control"
                        value={bankId}
                        onChange={(e) => setBankId(e.target.value)}
                        disabled={submitting}
                      >
                        <option value="">{inbound ? 'Select the account that was credited…' : 'Select a bank/cash account…'}</option>
                        {bankAccounts.map((a) => (
                          <option key={a.id} value={a.id}>
                            {a.bank_name} - {a.account_name}{a.account_number ? ` (${a.account_number})` : ''} · {formatCurrency(a.current_balance)}
                            {parseFloat(a.frozen_amount || 0) > 0 ? ` (${formatCurrency(a.frozen_amount)} frozen)` : ''}
                            {isBankInsufficient(a) ? ' · low balance' : ''}
                          </option>
                        ))}
                      </select>
                      {selectedBankInsufficient && (
                        <div style={{ marginTop: 4, fontSize: 12, color: '#9a6700', background: '#fff8e6', border: '1px solid #ffe69c', borderRadius: 4, padding: '4px 8px' }}>
                          {insufficientFundsMsg(selectedBank, effectiveSlipAmount)}
                        </div>
                      )}
                    </>
                  )}

                  {sourceType === 'customer' && (
                    !fundCustomer ? (
                      <div>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Search customer by name, number or nickname…"
                          value={fundQuery}
                          onChange={(e) => setFundQuery(e.target.value)}
                          disabled={submitting}
                        />
                        {fundSearching && <small className="form-text">Searching…</small>}
                        {fundResults.length > 0 && (
                          <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, marginTop: 6, maxHeight: 180, overflowY: 'auto' }}>
                            {fundResults.map((c) => (
                              <div
                                key={c.id}
                                onClick={() => selectFundCustomer(c)}
                                style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9' }}
                              >
                                <strong>{c.customer_name}</strong>
                                {c.customer_nickname ? ` (${c.customer_nickname})` : ''} — {c.customer_number}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 12px' }}>
                        <div>
                          <strong>{fundCustomer.customer_name}</strong>
                          {fundCustomer.customer_nickname ? ` (${fundCustomer.customer_nickname})` : ''} — {fundCustomer.customer_number}
                          <div style={{ fontSize: 13, color: '#6b7280' }}>
                            {fundBalance === null ? 'Loading balance…' : `Ledger balance: ${formatCurrency(fundBalance)}`}
                          </div>
                        </div>
                        <button type="button" className="btn btn-secondary" onClick={() => { setFundCustomer(null); setFundBalance(null); }} disabled={submitting} style={{ padding: '2px 10px' }}>
                          Change
                        </button>
                      </div>
                    )
                  )}
                  {errors.source && <span className="error-message">{errors.source}</span>}
                  <small className="form-text">
                    {inbound
                      ? 'Money IN: pick the account you can see the customer\'s payment credited in. Confirming the PR credits it immediately; it stays booked as "' + UNATTRIBUTED_HOLDING + '" (NAV-neutral) until the platform account handler releases the crypto and completes the trade.'
                      : sourceType === 'customer'
                        ? 'Paying from a customer account: no company bank money moves — that customer fronts the payment and we record that we owe them.'
                        : sourceType === 'bank'
                          ? 'Paying from a bank/cash account debits that account.'
                          : 'Choose whether this payment leaves a bank/cash account or is fronted by another customer.'}
                  </small>
                </div>

                <div className="form-group">
                  <label className="form-label">{inbound ? <>Bank-in / statement proof (optional)</> : <>Payment receipt for this slip <span className="required">*</span></>}</label>
                  {/* Was the app's last bare <input type="file"> (upload audit
                      2026-08-13) — no drop target. handleFile keeps owning
                      compression + the preview object URL. */}
                  <LocalImageDropBox onFile={handleFile} selectedFile={receiptFile} disabled={submitting} />
                  <small className="form-text">Or just paste the screenshot (Ctrl/Cmd+V) anywhere in this window.</small>
                  {/* The box previews the picked file itself now (LocalImageDropBox
                      showPreview default) — the external duplicate is gone;
                      previewUrl lives on for the staged-slips list below. */}
                  {errors.receipt && <span className="error-message">{errors.receipt}</span>}
                </div>

                <div className="form-actions">
                  <button
                    type="button"
                    className="btn btn-outline"
                    onClick={handleAddAnother}
                    disabled={submitting || leftToAllocate <= 0.01}
                    title="Stage this slip and add another payment for the same request"
                  >
                    {inbound ? '+ Add another PR' : '+ Add another payment'}
                  </button>
                  {/* A low balance is the REMINDER above, never a lock (operator
                      2026-10-09 ×2: the reminder shipped but these two buttons
                      stayed greyed out — "review & prepare button not available").
                      The server's hard floor is on Mark complete only. */}
                  <button type="submit" className="btn btn-primary" disabled={submitting}>
                    {inbound ? 'Confirm payment received' : <>Review &amp; prepare</>}{staged.length > 0 ? ` (${staged.length + (slipFormTouched() ? 1 : 0)})` : ''}
                  </button>
                  <button type="button" onClick={() => !submitting && onClose()} className="btn btn-secondary" disabled={submitting}>Close</button>
                </div>
              </form>
            ) : (
              // ---- Confirmation summary: exactly what will happen ----
              <div>
                <div style={{ border: '1px solid #f59e0b', background: '#fffbeb', borderRadius: 8, padding: 14, marginBottom: 12 }}>
                  <strong style={{ display: 'block', marginBottom: 8 }}>
                    {inbound
                      ? <>Confirm PR — {staged.length === 1 ? 'this payment' : `these ${staged.length} payments`} will be recorded AND credited now</>
                      : <>Confirm — this PREPARES {staged.length === 1 ? 'this payment' : `these ${staged.length} payments`} (no money moves yet)</>}
                  </strong>
                  <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.7 }}>
                    {staged.map((s) => (
                      <li key={s.key}>
                        {inbound
                          ? <>Confirm PR: <strong>{formatCurrency(s.amount)}</strong> received into <strong>{s.bankLabel || 'bank/cash'}</strong> — credited immediately on submit.</>
                          : <>Prepare <strong>{formatCurrency(s.amount)}</strong>{' '}
                            {s.source_type === 'customer'
                              ? <>— <strong>{s.fundCustomerName}</strong> will front it (we record we owe them only when you mark it complete).</>
                              : <>from <strong>{s.bankLabel || 'bank/cash'}</strong> (that account is debited only when you mark it complete).</>}</>}
                      </li>
                    ))}
                    <li>{confirmReceiptLine}</li>
                    <li>
                      {stagedCompletes
                        ? <strong>This prepares the full requested amount.</strong>
                        : <>After this, <strong>{formatCurrency(remaining - stagedSum)}</strong> remains to prepare.</>}
                    </li>
                  </ul>
                </div>
                <div className="form-actions">
                  <button type="button" className="btn btn-primary" onClick={handleConfirm} disabled={submitting}>
                    {submitting ? 'Processing…' : (inbound ? `Confirm PR — ${formatCurrency(stagedSum)}` : `Confirm & prepare ${formatCurrency(stagedSum)}`)}
                  </button>
                  <button type="button" className="btn btn-secondary" onClick={() => setConfirming(false)} disabled={submitting}>
                    Back
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Proof column. A payout shows the customer's payee-details proof; a
              money-IN platform sell shows the pack finance must actually verify
              (their payment receipt AND the platform order screenshot). Same
              column, list-driven — no second modal. */}
          {proofList.length > 0 && !fullyPrepared && (
            <div style={{ flex: '1 1 300px', minWidth: 260, maxWidth: 400 }}>
              {proofsNote && (
                <div style={{ marginBottom: 8, padding: '6px 10px', borderRadius: 6, background: '#eff6ff', border: '1px solid #bfdbfe', fontSize: 12, color: '#1e40af' }}>
                  {proofsNote}
                </div>
              )}
              {proofList.map((p, i) => (
                <div key={p.url || i} style={{ marginBottom: i < proofList.length - 1 ? 14 : 0 }}>
                  <label className="form-label" style={{ display: 'block', marginBottom: 6 }}>{p.label}</label>
                  <a href={resolveUploadUrl(p.url)} target="_blank" rel="noopener noreferrer" title="Open full size">
                    <img
                      src={resolveUploadUrl(p.url)}
                      alt={p.label}
                      style={{ width: '100%', maxHeight: 480, objectFit: 'contain', borderRadius: 8, border: '1px solid #e5e7eb', background: '#f9fafb' }}
                    />
                  </a>
                </div>
              ))}
              <small className="form-text">Click an image to open it full size.</small>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default FulfillPaymentModal;
